/**
 * Studio Command Palette (⌘K Assistant)
 * Static suggestions routing to existing views — no LLM
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icn } from "../theme";

interface Suggestion {
	label: string;
	description: string;
	path: string;
}

const SUGGESTIONS: Suggestion[] = [
	{
		label: "Go to Home",
		description: "View your studio pulse and summary",
		path: "/dashboard",
	},
	{
		label: "View Requests",
		description: "Review incoming project opportunities",
		path: "/requests",
	},
	{
		label: "Browse Projects",
		description: "See all active and completed projects",
		path: "/projects",
	},
	{
		label: "Open Portfolio",
		description: "Manage your public portfolio",
		path: "/portfolio",
	},
	{
		label: "Explore Catalog",
		description: "Browse materials and products",
		path: "/catalog",
	},
];

interface AssistantProps {
	open: boolean;
	onClose: () => void;
}

export function StudioAssistant({ open, onClose }: AssistantProps) {
	const navigate = useNavigate();
	const [query, setQuery] = useState("");
	const [selectedIndex, setSelectedIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	const filtered = SUGGESTIONS.filter(
		(s) =>
			s.label.toLowerCase().includes(query.toLowerCase()) ||
			s.description.toLowerCase().includes(query.toLowerCase()),
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!open) return;

			if (e.key === "Escape") {
				onClose();
			} else if (e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIndex((i) => Math.max(i - 1, 0));
			} else if (e.key === "Enter" && filtered[selectedIndex]) {
				navigate(filtered[selectedIndex].path);
				onClose();
			}
		},
		[open, onClose, filtered, selectedIndex, navigate],
	);

	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	useEffect(() => {
		if (open) {
			setQuery("");
			setSelectedIndex(0);
		}
	}, [open]);

	// Focused from an effect rather than with `autoFocus`. The palette can
	// open in the same update that closes the phone menu sheet, and
	// `autoFocus` runs while that sheet's focus trap is still listening: the
	// trap pulls focus back into the closing sheet, which then unmounts and
	// leaves it on <body>. The effect runs after the trap has let go.
	useEffect(() => {
		if (open) inputRef.current?.focus();
	}, [open]);

	if (!open) return null;

	return (
		<button
			type="button"
			aria-label="Close assistant"
			style={{
				position: "fixed",
				inset: 0,
				// The popup layer, above the modal layer (9999): the palette can
				// open while the phone menu sheet is still playing its close
				// animation, and must not sit under it.
				zIndex: 10000,
				display: "flex",
				alignItems: "flex-start",
				justifyContent: "center",
				paddingTop: "15vh",
				background:
					"color-mix(in srgb, var(--color-dashboard-shadow-ink) 50%, transparent)",
				backdropFilter: "blur(8px)",
				border: "none",
				cursor: "default",
			}}
			onClick={onClose}
		>
			<div
				role="dialog"
				aria-modal="true"
				aria-label="Command palette"
				style={{
					width: "100%",
					maxWidth: 640,
					margin: "0 16px",
					background: "var(--color-dashboard-paper)",
					borderRadius: 13,
					border: "1px solid var(--color-dashboard-border)",
					boxShadow:
						"0 24px 60px -14px color-mix(in srgb, var(--color-dashboard-shadow-ink) 22%, transparent), 0 4px 8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 5%, transparent)",
					overflow: "hidden",
					animation: "modalIn 200ms cubic-bezier(0.22, 1, 0.36, 1)",
				}}
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						onClose();
					}
				}}
			>
				{/* Search input */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						padding: "16px 20px",
						borderBottom: "1px solid var(--color-dashboard-border)",
					}}
				>
					<Icn
						name="search"
						size={20}
						style={{ color: "var(--color-dashboard-muted)", flexShrink: 0 }}
					/>
					<input
						ref={inputRef}
						type="text"
						placeholder="Jump to a section of your studio…"
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setSelectedIndex(0);
						}}
						style={{
							flex: 1,
							border: "none",
							outline: "none",
							background: "transparent",
							fontSize: 16,
							fontFamily: "var(--font-body)",
							color: "var(--color-dashboard-text)",
						}}
					/>
					<span
						style={{
							fontSize: 12,
							fontFamily: "var(--font-mono)",
							color: "var(--color-dashboard-muted)",
							background: "var(--color-dashboard-divider)",
							padding: "4px 8px",
							borderRadius: 4,
						}}
					>
						ESC
					</span>
				</div>

				{/* Suggestions */}
				<div style={{ maxHeight: 360, overflowY: "auto" }}>
					<div
						style={{
							padding: "12px 20px 8px",
							fontSize: 11,
							fontFamily: "var(--font-mono)",
							fontWeight: 600,
							letterSpacing: "0.12em",
							textTransform: "uppercase",
							color: "var(--color-dashboard-muted)",
						}}
					>
						SUGGESTED
					</div>

					{filtered.length === 0 ? (
						<div
							style={{
								padding: "24px 20px",
								textAlign: "center",
								color: "var(--color-dashboard-muted)",
								fontSize: 14,
								fontFamily: "var(--font-body)",
							}}
						>
							No results found
						</div>
					) : (
						filtered.map((suggestion, index) => (
							<button
								type="button"
								key={suggestion.path}
								onClick={() => {
									navigate(suggestion.path);
									onClose();
								}}
								style={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									gap: 14,
									padding: "12px 20px",
									border: "none",
									background:
										index === selectedIndex
											? "color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent)"
											: "transparent",
									cursor: "pointer",
									textAlign: "left",
									transition: "background 150ms",
								}}
								onMouseEnter={() => setSelectedIndex(index)}
							>
								<div
									style={{
										width: 36,
										height: 36,
										borderRadius: 7,
										background:
											index === selectedIndex
												? "var(--color-dashboard-accent)"
												: "var(--color-dashboard-divider)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
									}}
								>
									<Icn
										name="arrowRight"
										size={16}
										style={{
											color:
												index === selectedIndex
													? "var(--color-dashboard-on-fill)"
													: "var(--color-dashboard-muted-dark)",
										}}
									/>
								</div>
								<div>
									<div
										style={{
											fontSize: 14,
											fontFamily: "var(--font-body)",
											fontWeight: 500,
											color: "var(--color-dashboard-text)",
										}}
									>
										{suggestion.label}
									</div>
									<div
										style={{
											fontSize: 12,
											fontFamily: "var(--font-body)",
											color: "var(--color-dashboard-muted)",
											marginTop: 2,
										}}
									>
										{suggestion.description}
									</div>
								</div>
							</button>
						))
					)}
				</div>

				{/* Footer */}
				<div
					style={{
						display: "flex",
						gap: 16,
						padding: "12px 20px",
						borderTop: "1px solid var(--color-dashboard-border)",
						background: "var(--color-dashboard-surface)",
					}}
				>
					<span
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							fontSize: 12,
							fontFamily: "var(--font-mono)",
							color: "var(--color-dashboard-muted)",
						}}
					>
						<span
							style={{
								padding: "2px 6px",
								background: "var(--color-dashboard-border)",
								borderRadius: 3,
							}}
						>
							↑
						</span>
						<span
							style={{
								padding: "2px 6px",
								background: "var(--color-dashboard-border)",
								borderRadius: 3,
							}}
						>
							↓
						</span>
						<span>navigate</span>
					</span>
					<span
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							fontSize: 12,
							fontFamily: "var(--font-mono)",
							color: "var(--color-dashboard-muted)",
						}}
					>
						<span
							style={{
								padding: "2px 6px",
								background: "var(--color-dashboard-border)",
								borderRadius: 3,
							}}
						>
							↵
						</span>
						<span>select</span>
					</span>
				</div>
			</div>
		</button>
	);
}

// Global ⌘K listener hook
export function useCommandPalette(onToggle: () => void) {
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === "k") {
				e.preventDefault();
				onToggle();
			}
		};

		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [onToggle]);
}
